/**
 * useAudioOutput — AirPlay 2 / audio output picker.
 *
 * Supports:
 * - Chrome 105+ desktop: navigator.mediaDevices.selectAudioOutput() native picker
 * - iOS Safari: <audio webkit-airplay="allow"> — AirPlay icon shows in native control center / lockscreen
 * - Chromium: HTMLMediaElement.setSinkId() — direct device selection by deviceId
 *
 * Usage:
 *   const { sinkId, supported, picking, pickOutput, reset } = useAudioOutput([audioRefA, audioRefB]);
 *
 * sinkId is persisted to localStorage under melodiza:audio-sink-id.
 * On mount, if a saved sinkId exists, it is re-applied to all audio refs.
 */

import { useState, useCallback, useEffect } from 'react';
import { capture } from '../lib/posthog';

const STORAGE_KEY = 'melodiza:audio-sink-id';

export function useAudioOutput(audioRefs = []) {
  const [sinkId, setSinkId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [supported, setSupported] = useState(false);
  const [picking, setPicking] = useState(false);

  // Feature detection
  useEffect(() => {
    const audio0 = audioRefs[0]?.current;
    const hasSetSinkId = typeof audio0?.setSinkId === 'function';
    const hasSelectAudioOutput =
      typeof navigator.mediaDevices?.selectAudioOutput === 'function';
    setSupported(hasSetSinkId || hasSelectAudioOutput);
  }, []); // eslint-disable-line

  // Apply sinkId to all audio refs whenever it changes
  useEffect(() => {
    if (!sinkId) return;
    audioRefs.forEach((ref) => {
      const audio = ref?.current;
      if (audio && typeof audio.setSinkId === 'function') {
        audio
          .setSinkId(sinkId)
          .catch((e) => console.warn('[useAudioOutput] setSinkId failed', e));
      }
    });
    try {
      localStorage.setItem(STORAGE_KEY, sinkId);
    } catch {}
  }, [sinkId]); // eslint-disable-line

  const pickOutput = useCallback(async () => {
    capture('airplay_picker_opened', { ua: navigator.userAgent });
    setPicking(true);
    try {
      if (typeof navigator.mediaDevices?.selectAudioOutput === 'function') {
        // Chrome 105+ — opens native audio output picker
        const device = await navigator.mediaDevices.selectAudioOutput();
        if (device?.deviceId) setSinkId(device.deviceId);
      } else {
        // iOS Safari: AirPlay is handled via webkit-airplay="allow" attribute on <audio>.
        // The AirPlay icon appears natively in control center and lockscreen.
        // No programmatic picker available on iOS — user uses system controls.
        console.info('[useAudioOutput] iOS Safari — AirPlay via native control center');
      }
    } catch (e) {
      // NotAllowedError: user dismissed picker — not an error condition
      if (e.name !== 'NotAllowedError') console.error('[useAudioOutput] picker error', e);
    } finally {
      setPicking(false);
    }
  }, []);

  const reset = useCallback(() => {
    setSinkId('');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  return { sinkId, supported, picking, pickOutput, reset };
}
